import React, { useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form'
import { useMutation } from '@tanstack/react-query'
import { ApiError } from '@/lib/api/types'
import { paymentIntentApi } from '@/lib/api/paymentIntentApi'
import { oauthApi } from '@/lib/api/oauthApi'
import { Trash2, Plus, Key, Eye, EyeOff } from 'lucide-react'
import { ErrorDisplay } from '../error-display'
import { setAuthToken } from '@/lib/api/apiClient'
import { CreatePaymentIntentRequest, CreatePaymentIntentRequestCurrencyEnum, PaymentIntentResponse } from '@/lib/api/generated/models'
import { GetOAuthTokenRequest } from '@/lib/api/generated/models'

const lineItemSchema = z.object({
  name: z.string().min(1, 'Item name is required'),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  unitPrice: z.number().min(0, 'Unit price must be non-negative'),
  totalPrice: z.number().min(0, 'Total price must be non-negative'),
})

const metadataItemSchema = z.object({
  key: z.string().min(1, 'Key is required').max(100, 'Key must be 100 characters or less'),
  value: z.string().min(1, 'Value is required').max(255, 'Value must be 255 characters or less'),
})

const paymentIntentSchema = z.object({
  // OAuth credentials
  clientId: z.string().min(1, 'Client ID is required'),
  clientSecret: z.string().min(1, 'Client Secret is required'),
  scope: z.string().optional(),

  // Payment intent data
  merchantId: z.string().min(1, 'Merchant Account ID is required'),
  totalAmount: z.number().min(1, 'Total amount must be greater than 0'),
  subtotal: z.number().min(1, 'Subtotal must be greater than 0'),
  tax: z.number().optional(),
  fees: z.number().optional(),
  currency: z.string().length(3, 'Currency must be 3 characters'),
  merchantReference: z.string().optional(),
  returnUrl: z.string().url('Return URL must be a valid URL'),
  cancelUrl: z.string().url('Cancel URL must be a valid URL').optional(),
  description: z.string().optional(),
  customerName: z.string().min(1, 'Customer name is required'),
  customerEmail: z.string().email('Valid email is required'),
  customerExternalId: z.string().optional(),
  billingLine1: z.string().min(1, 'Address line 1 is required'),
  billingLine2: z.string().optional(),
  billingCity: z.string().min(1, 'City is required'),
  billingState: z.string().optional(),
  billingPostalCode: z.string().min(1, 'Postal code is required'),
  billingCountry: z.string().length(2, 'Country must be 2 characters'),
  orderId: z.string().min(1, 'Order ID is required'),
  lineItems: z.array(lineItemSchema).min(1, 'At least one line item is required'),
  metadata: z.array(metadataItemSchema).optional(),
})

type FormData = z.infer<typeof paymentIntentSchema>

export function PaymentIntentForm({
  onPaymentIntentCreated,
  scenarioData,
}: {
  onPaymentIntentCreated: (response: PaymentIntentResponse) => void
  scenarioData?: FormData
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showClientSecret, setShowClientSecret] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)

  const form = useForm<FormData>({
    resolver: zodResolver(paymentIntentSchema),
    defaultValues: scenarioData || {
      clientId: '',
      clientSecret: '',
      scope: '',
      merchantId: '',
      totalAmount: 0,
      subtotal: 0,
      tax: 0,
      fees: 0,
      currency: CreatePaymentIntentRequestCurrencyEnum.Usd,
      merchantReference: '',
      returnUrl: '',
      cancelUrl: '',
      description: '',
      customerName: '',
      customerEmail: '',
      customerExternalId: '',
      billingLine1: '',
      billingLine2: '',
      billingCity: '',
      billingState: '',
      billingPostalCode: '',
      billingCountry: 'US',
      orderId: `ORDER_${Date.now()}`,
      lineItems: [
        {
          name: '',
          quantity: 1,
          unitPrice: 0,
          totalPrice: 0,
        },
      ],
      metadata: [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'lineItems',
  })

  const {
    fields: metadataFields,
    append: appendMetadata,
    remove: removeMetadata,
  } = useFieldArray({
    control: form.control,
    name: 'metadata',
  })

  const formValues = form.watch()

  const lineItemsDep = JSON.stringify(
    formValues.lineItems?.map((item) => ({ quantity: item.quantity, unitPrice: item.unitPrice })) || [],
  )

  // Calculate totals when line items change
  useEffect(() => {
    const lineItems = formValues.lineItems || []

    // Calculate total price for each line item
    const lineItemsWithTotals = lineItems.map((item) => ({
      ...item,
      totalPrice: (item.quantity || 0) * (item.unitPrice || 0),
    }))

    const subtotal = lineItemsWithTotals.reduce((sum, item) => sum + item.totalPrice, 0)
    const tax = formValues.tax || 0
    const fees = formValues.fees || 0
    const totalAmount = subtotal + tax + fees

    form.setValue('subtotal', subtotal)
    form.setValue('totalAmount', totalAmount)

    // Update line item total prices
    lineItemsWithTotals.forEach((item, index) => {
      form.setValue(`lineItems.${index}.totalPrice`, item.totalPrice, { shouldValidate: false })
    })
  }, [lineItemsDep, formValues.tax, formValues.fees, form])

  const oauthTokenMutation = useMutation({
    mutationFn: async (request: GetOAuthTokenRequest) => {
      const response = await oauthApi.getAccessToken(request)
      setAuthToken(response.access_token)
      return response.access_token
    },
  })

  const paymentIntentMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const tokenRequest: GetOAuthTokenRequest = {
        grant_type: 'client_credentials',
        client_id: data.clientId,
        client_secret: data.clientSecret,
        scope: data.scope || 'payment:create',
      }

      // Get the access token first
      await oauthTokenMutation.mutateAsync(tokenRequest)

      const paymentIntentRequest: CreatePaymentIntentRequest = {
        merchantId: data.merchantId,
        totalAmount: data.totalAmount,
        subtotal: data.subtotal,
        tax: data.tax,
        fees: data.fees,
        currency: data.currency as CreatePaymentIntentRequestCurrencyEnum,
        merchantReference: data.merchantReference,
        returnUrl: data.returnUrl,
        cancelUrl: data.cancelUrl,
        description: data.description,
        customer: {
          name: data.customerName,
          email: data.customerEmail,
          externalId: data.customerExternalId,
        },
        billingAddress: {
          line1: data.billingLine1,
          line2: data.billingLine2,
          city: data.billingCity,
          state: data.billingState,
          postalCode: data.billingPostalCode,
          country: data.billingCountry,
        },
        orderDetails: {
          orderId: data.orderId,
          lineItems: data.lineItems.map((item) => ({
            name: item.name,
            description: item.name,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            totalPrice: item.totalPrice,
          })),
        },
        metadata: data.metadata?.reduce((acc, item) => {
          if (item.key && item.value) {
            acc[item.key] = item.value
          }
          return acc
        }, {} as Record<string, string>),
      }

      // Create the payment intent with the access token
      return paymentIntentApi.createPaymentIntent(paymentIntentRequest)
    },
    onError: (error: ApiError) => {
      setError(error)
      console.error('Error creating payment intent:', error)
    },
    onSuccess: (response: PaymentIntentResponse) => {
      onPaymentIntentCreated(response)
      console.log(response)
    },
  })

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    setError(null) // Clear previous errors
    try {
      await paymentIntentMutation.mutateAsync(data)
    } catch (err: any) {
      setError(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const addLineItem = () => {
    append({
      name: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
    })
  }

  const removeLineItem = (index: number) => {
    if (fields.length > 1) {
      remove(index)
    }
  }

  return (
    <div className="space-y-6">
      {/* Display API Errors */}
      <ErrorDisplay error={error} onDismiss={() => setError(null)} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* OAuth Credentials Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                OAuth Credentials
              </CardTitle>
              <CardDescription>Client credentials for OAuth token retrieval</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="clientId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client ID *</FormLabel>
                      <FormControl>
                        <Input placeholder="pk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="clientSecret"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Secret *</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showClientSecret ? 'text' : 'password'}
                            placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowClientSecret(!showClientSecret)}
                          >
                            {showClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="scope"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Scope</FormLabel>
                    <FormControl>
                      <Input placeholder="payment:create payment:read" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Merchant Configuration</CardTitle>
              <CardDescription>Merchant account and currency settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="merchantId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant Account ID *</FormLabel>
                      <FormControl>
                        <Input placeholder="XXXXXXXXXXXXXXXXXXXXXXXXXXXX" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency *</FormLabel>
                      <FormControl>
                        <Input placeholder="USD" maxLength={3} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>URLs</CardTitle>
              <CardDescription>Success and cancel URLs for the payment flow</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="returnUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Return URL *</FormLabel>
                    <FormControl>
                      <Input placeholder="https://merchant.com/payment/success" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cancelUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cancel URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://merchant.com/payment/cancel" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
              <CardDescription>Customer details for the payment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="customerName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="John Smith" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="customerEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john.smith@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="customerExternalId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer External ID</FormLabel>
                    <FormControl>
                      <Input placeholder="CUST_12345" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Billing Address</CardTitle>
              <CardDescription>Customer billing address information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="billingLine1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address Line 1 *</FormLabel>
                    <FormControl>
                      <Input placeholder="123 Main Street" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billingLine2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address Line 2</FormLabel>
                    <FormControl>
                      <Input placeholder="Apt 4B" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="billingCity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City *</FormLabel>
                      <FormControl>
                        <Input placeholder="New York" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="billingState"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <FormControl>
                        <Input placeholder="NY" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="billingPostalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Postal Code *</FormLabel>
                      <FormControl>
                        <Input placeholder="10001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="billingCountry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country *</FormLabel>
                    <FormControl>
                      <Input placeholder="US" maxLength={2} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
              <CardDescription>Payment amounts and metadata</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormItem>
                  <FormLabel>Total Amount *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="2500"
                      value={formValues.totalAmount}
                      readOnly
                      className="bg-gray-50"
                    />
                  </FormControl>
                </FormItem>

                <FormItem>
                  <FormLabel>Subtotal *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="2000"
                      value={formValues.subtotal}
                      readOnly
                      className="bg-gray-50"
                    />
                  </FormControl>
                </FormItem>

                <FormField
                  control={form.control}
                  name="tax"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tax</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="200"
                          value={field.value || ''}
                          onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                          onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="fees"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fees</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="100"
                          value={field.value || ''}
                          onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                          onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="merchantReference"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant Reference</FormLabel>
                      <FormControl>
                        <Input placeholder="ORDER_12345" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input placeholder="Payment for concert tickets" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Order Details</CardTitle>
              <CardDescription>Order information and line items</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="orderId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Order ID *</FormLabel>
                    <FormControl>
                      <Input placeholder="ORD_20240701_001" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Line Items</h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLineItem}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Item
                  </Button>
                </div>

                {fields.map((field, index) => (
                  <div key={field.id} className="border rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <h5 className="font-medium">Item {index + 1}</h5>
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeLineItem(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`lineItems.${index}.name`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Item Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="Concert Ticket" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`lineItems.${index}.quantity`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Quantity *</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="1"
                                min="1"
                                value={field.value || ''}
                                onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                                onChange={(e) =>
                                  field.onChange(e.target.value === '' ? 1 : parseInt(e.target.value) || 1)
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <FormField
                        control={form.control}
                        name={`lineItems.${index}.unitPrice`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Unit Price *</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="2500"
                                min="0"
                                value={field.value || ''}
                                onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                                onChange={(e) =>
                                  field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormItem>
                        <FormLabel>Total Price *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="2500"
                            min="0"
                            value={
                              (formValues.lineItems?.[index]?.quantity || 0) *
                              (formValues.lineItems?.[index]?.unitPrice || 0)
                            }
                            readOnly
                            className="bg-gray-50"
                          />
                        </FormControl>
                      </FormItem>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
              <CardDescription>Optional key-value pairs for additional payment context</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Metadata Fields</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => appendMetadata({ key: '', value: '' })}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Metadata
                </Button>
              </div>

              {metadataFields.map((field, index) => (
                <div key={field.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-medium">Metadata {index + 1}</h5>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeMetadata(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name={`metadata.${index}.key`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Key *</FormLabel>
                          <FormControl>
                            <Input placeholder="campaign_id" maxLength={100} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`metadata.${index}.value`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Value *</FormLabel>
                          <FormControl>
                            <Input placeholder="SUMMER2024" maxLength={255} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}

              {metadataFields.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No metadata fields added</p>
                  <p className="text-sm">Click "Add Metadata" to include additional context</p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button type="submit" disabled={isSubmitting || paymentIntentMutation.isPending}>
              {isSubmitting || paymentIntentMutation.isPending ? 'Creating Payment Intent...' : 'Create Payment Intent'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.reset()
                setError(null) // Clear errors when resetting
              }}
            >
              Reset Form
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
